// Diário › Meu Dia (0.65): uma página por dia, com humor, clima, a página infinita igual à de Estudos,
// fotos e vídeos, perguntas para quando não souber o que escrever, o resumo do dia vindo das outras abas
// e "Neste dia" (um mês e um ano antes).
import { useEffect, useMemo, useState } from 'react';
import { Ditado } from '../Ditado';
import { Alert, Linking, Pressable, Text, TextInput, View } from 'react-native';
import { useAllDocs } from '../../store/store';
import { confDiario, diasEscritos, gravarDia, lerDia, type Video, lerFrase, lerHabitos, lerInsp, mudarConf, useDiario, type DiaDiario, type Resposta } from '../../store/diario';
import { AnexosEditor } from '../Anexos';
import { QuadroPagina } from '../QuadroPagina';
import { PaginaRolavel } from '../PaginaEditor';
import { ScrollArrastavel } from '../Arrastar';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DesenhoView } from '../Desenho';
import { PickerModal } from '../Pickers';
import { Chip, Field, Screen, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { Barra, CalendarioMes, HUMORES, Recolhe, dataLonga, emojiHumor, tarefaDe } from './comum';
import { CONDICOES, climaAgora, emojiCond, textoClima } from '../../lib/clima';
import { feitoEm } from '../../lib/habitos';
import { occursOn } from '../../lib/recur';
import { totalAgua } from '../../store/bemestar';
import { addDays, addMonths, horaTela, parseYmd, today, uid, ymd } from '../../lib/date';
import { N_, t } from '../../lib/i18n';
import { useBotaoNovo } from '../../lib/botaoNovo';
import { semBloqueio } from '../../auth/AuthContext';
import { oferecerDesfazer } from '../../lib/desfazer';
import { useCaixas } from '../../lib/caixas';

export const PERGUNTAS = [N_('O que foi bom hoje?'), N_('O que quero melhorar amanhã?'), N_('Pelo que sou grata hoje?'), N_('O que aprendi hoje?'),
  N_('Como cuidei de mim hoje?'), N_('Quem fez o meu dia melhor?')];

/** texto 12: a página do dia rola pela PaginaRolavel (a barra do editor fica fixa no topo, abaixo da hora/bateria, como em Estudos). */
function RolagemDia(p: any) {
  const ins = useSafeAreaInsets();
  return <PaginaRolavel {...p} Rolagem={ScrollArrastavel} topoExtra={ins.top} />;
}

export function MeuDia({ data: ini, cor, onBack }: { data?: string; cor: string; onBack: () => void }) {
  useDiario();
  const docs = useAllDocs();
  const [data, setData] = useState(ini ?? today());
  // 11d F: o + leva à página de hoje (já nela: nada a adicionar, o + fica apagadinho)
  useBotaoNovo(data !== today() ? { rotulo: t('Nova página do dia'), fn: () => setData(today()) } : null);
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const [cal, setCal] = useState(false);
  const [climaEd, setClimaEd] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [hora, setHora] = useState(false);
  const dia = lerDia(data);
  const conf = confDiario();
  const mudar = (p: Partial<DiaDiario>) => gravarDia(data, { ...lerDia(data), ...p });

  // clima automático: só no dia de hoje, se ainda não tiver
  useEffect(() => {
    if (data !== today() || dia.clima || !conf.clima) return;
    let vivo = true;
    setBuscando(true);
    climaAgora().then(c => { if (vivo && c && !lerDia(data).clima) mudar({ clima: { temp: c.temp, cond: c.cond } }); }).finally(() => vivo && setBuscando(false));
    return () => { vivo = false; };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const fraseId = lerFrase();
  const frase = fraseId ? lerInsp().find(x => x.id === fraseId) : null;
  const marcados = useMemo(() => new Set(diasEscritos()), [docs.diariop]); // eslint-disable-line react-hooks/exhaustive-deps

  // resumo do dia (só leitura) vindo das outras abas
  const tarefas = docs.planner.tasks.filter(x => x.done.includes(data) && occursOn(x, data));
  const minEstudo = docs.estudos.sessoes.filter(s => s.data === data).reduce((s, x) => s + x.min, 0);
  const b = docs.bemestar;
  const aguaMl = totalAgua(b, data), metaMl = b.metaMl ?? (b.metaAgua || 8) * (b.copoMl ?? 250);
  const habitos = lerHabitos().filter(h => !h.formado);
  const cumpridos = habitos.filter(h => feitoEm(h, data, docs, tarefaDe('hab:' + h.id)));

  const respostas = dia.respostas ?? [];
  const usar = (p: string) => { if (respostas.some(r => r.pergunta === p)) return; mudar({ respostas: [...respostas, { id: uid(), pergunta: p, texto: '' }] }); };
  /** Tirar pergunta ou vídeo (texto 05 C1): só o Desfazer de 5 segundos, sem Lixeira (o diário é cifrado e não vai aberto para a Lixeira). */
  const tirarDoDia = <K extends 'respostas' | 'videos'>(campo: K, x: NonNullable<DiaDiario[K]>[number]) => {
    const d0 = data;
    const l0 = (lerDia(d0)[campo] ?? []) as { id: string }[];
    const pos = l0.findIndex(y => y.id === x.id);
    gravarDia(d0, { ...lerDia(d0), [campo]: l0.filter(y => y.id !== x.id) });
    oferecerDesfazer('excluir', () => {
      const l = (lerDia(d0)[campo] ?? []) as { id: string }[];
      if (l.some(y => y.id === x.id)) return;
      const p = pos < 0 || pos > l.length ? l.length : pos;
      gravarDia(d0, { ...lerDia(d0), [campo]: [...l.slice(0, p), x, ...l.slice(p)] });
    });
  };
  const mudarResp = (r: Resposta, texto: string) => mudar({ respostas: respostas.map(x => (x.id === r.id ? { ...x, texto } : x)) });
  const [vid, setVid] = useState({ titulo: '', url: '' });

  const antes = [{ rot: t('Há um mês'), d: ymd(addMonths(parseYmd(data), -1)) }, { rot: t('Há um ano'), d: ymd(addMonths(parseYmd(data), -12)) }].filter(x => marcados.has(x.d));

  return (
    <Screen title={t('Meu Dia')} onBack={onBack} rolagem={RolagemDia} right={
      <Pressable onPress={() => setCal(true)} style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}><Text style={{ fontSize: 18 }}>📅</Text></Pressable>}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Pressable onPress={() => setData(addDays(data, -1))} hitSlop={10} style={{ padding: 6 }}><Text style={{ color: C.text, fontSize: 20 }}>‹</Text></Pressable>
        <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontWeight: '800', fontSize: 16 }}>{data === today() ? t('Hoje') + ' · ' : ''}{dataLonga(data)}</Text>
        <Pressable onPress={() => setData(addDays(data, 1))} disabled={data >= today()} hitSlop={10} style={{ padding: 6, opacity: data >= today() ? 0.3 : 1 }}><Text style={{ color: C.text, fontSize: 20 }}>›</Text></Pressable>
      </View>

      {!!frase && (
        <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: cx.raio(14), ...cx.pads(12), borderLeftWidth: 4, borderLeftColor: cor }}>
          <Text style={{ color: C.text, fontStyle: 'italic', fontSize: 15 }}>“{frase.texto}”</Text>
        </View>
      )}

      {/* humor e clima */}
      <View style={{ backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(12), gap: cx.gap(10) }}>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('COMO ESTOU')}</Text>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          {HUMORES.map(([n, e, nome]) => (
            <Pressable key={n} onPress={() => mudar({ humor: dia.humor === n ? null : n })} accessibilityLabel={t(nome)}
              style={{ width: cx.v(46, 42, 36), height: cx.v(46, 42, 36), borderRadius: cx.v(23, 21, 18), alignItems: 'center', justifyContent: 'center', backgroundColor: dia.humor === n ? alpha(cor, 0.25) : 'transparent', borderWidth: dia.humor === n ? 2 : 0, borderColor: cor }}>
              <Text style={{ fontSize: cx.icone(26) }}>{e}</Text>
            </Pressable>
          ))}
        </View>
        <Pressable onPress={() => setClimaEd(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 20 }}>{emojiCond(dia.clima?.cond)}</Text>
          <Text style={{ color: dia.clima ? C.text : C.mute, flex: 1 }}>{dia.clima ? textoClima(dia.clima) : buscando ? t('Buscando o clima…') : t('Clima (toque para preencher)')}</Text>
          <Text style={{ color: C.mute, fontSize: 12 }}>✏️</Text>
        </Pressable>
      </View>

      {/* perguntas opcionais */}
      <View style={{ gap: 6 }}>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Sem ideia do que escrever? Toque numa pergunta:')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {PERGUNTAS.filter(p => !respostas.some(r => r.pergunta === p)).map(p => <Chip key={p} small cor={cor} on={false} label={t(p)} onPress={() => usar(p)} />)}
        </View>
        {respostas.map(r => (
          <View key={r.id} style={{ backgroundColor: C.card, borderRadius: cx.raio(14), padding: cx.pad(10), gap: cx.gap(4) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={{ color: cor, fontWeight: '800', flex: 1 }}>{t(r.pergunta)}</Text>
              <Pressable hitSlop={8} onPress={() => Alert.alert(t('Tirar esta pergunta?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Tirar'), style: 'destructive', onPress: () => tirarDoDia('respostas', r) }])}><Text style={{ color: C.mute }}>✕</Text></Pressable>
            </View>
            <View>
              <TextInput multiline value={r.texto} onChangeText={v => mudarResp(r, v)} placeholder={t('Escreva aqui')} placeholderTextColor={C.mute}
                style={{ color: C.text, fontSize: 15, minHeight: 44, textAlignVertical: 'top', paddingRight: 36 }} />
              <View style={{ position: 'absolute', right: 0, bottom: 0 }}><Ditado pequeno cor={cor} onTexto={tx => mudarResp(r, r.texto ? r.texto + ' ' + tx : tx)} /></View>
            </View>
          </View>
        ))}
      </View>

      {/* página infinita (igual à de Estudos) */}
      <QuadroPagina valor={dia.quadro} onChange={q => mudar({ quadro: q })} cor={cor} aba="diario" altura={420} encherTela nome={t('Meu Dia') + ' ' + data} />

      {/* fotos e vídeos */}
      <Recolhe titulo={`📷 ${t('Fotos do dia')}${dia.fotos?.length ? ` (${dia.fotos.length})` : ''}`}>
        <AnexosEditor lista={dia.fotos ?? []} cor={cor} onChange={l => mudar({ fotos: l })} aviso={false} />
      </Recolhe>
      <Recolhe titulo={`🎬 ${t('Vídeos')}${dia.videos?.length ? ` (${dia.videos.length})` : ''}`}>
        {(dia.videos ?? []).map(v => (
          <View key={v.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: C.card, borderRadius: cx.raio(12), padding: cx.pad(10) }}>
            <Pressable style={{ flex: 1 }} onPress={() => semBloqueio(() => Linking.openURL(v.url)).catch(() => Alert.alert(t('Não foi possível abrir')))}>
              <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={1}>▶️ {v.titulo || v.url}</Text>
              <Text style={{ color: C.sub, fontSize: 11 }} numberOfLines={1}>{v.url}</Text>
            </Pressable>
            <Pressable hitSlop={8} onPress={() => Alert.alert(t('Tirar este vídeo?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Tirar'), style: 'destructive', onPress: () => tirarDoDia('videos', v as Video) }])}><Text style={{ color: C.mute }}>✕</Text></Pressable>
          </View>
        ))}
        <Field value={vid.titulo} onChangeText={v => setVid(x => ({ ...x, titulo: v }))} placeholder={t('Nome do vídeo (opcional)')} />
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Field value={vid.url} onChangeText={v => setVid(x => ({ ...x, url: v.trim() }))} placeholder={t('Link do vídeo (YouTube, Instagram...)')} autoCapitalize="none" autoCorrect={false} keyboardType="url" style={{ flex: 1 }} />
          <Chip cor={cor} on label={t('Adicionar')} onPress={() => {
            if (!/^https?:\/\/\S+/i.test(vid.url)) return Alert.alert(t('Cole um link que comece com https://'));
            mudar({ videos: [...(dia.videos ?? []), { id: uid(), titulo: vid.titulo.trim(), url: vid.url }] }); setVid({ titulo: '', url: '' });
          }} />
        </View>
      </Recolhe>

      {/* resumo do dia, das outras abas (só leitura) */}
      <Recolhe titulo={'📊 ' + t('Resumo do dia')}>
        <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
          <Text style={{ color: C.text }}>✅ {t('{n} tarefa(s) concluída(s) no My Planner', { n: tarefas.length })}</Text>
          {tarefas.slice(0, 8).map(x => <Text key={x.id} style={{ color: C.sub, fontSize: 13 }} numberOfLines={1}>   {x.emoji} {x.title}</Text>)}
          <Text style={{ color: C.text }}>📚 {t('Estudo: {h}', { h: minEstudo >= 60 ? `${Math.floor(minEstudo / 60)}h${String(minEstudo % 60).padStart(2, '0')}` : `${minEstudo} min` })}</Text>
          <Text style={{ color: C.text }}>💧 {t('Água: {a} de {m} ml', { a: aguaMl, m: metaMl })}</Text>
          <Barra p={metaMl ? aguaMl / metaMl : 0} cor="#06B6D4" alt={6} />
          <Text style={{ color: C.text }}>🔥 {t('Hábitos cumpridos: {f} de {n}', { f: cumpridos.length, n: habitos.length })}</Text>
          {cumpridos.map(h => <Text key={h.id} style={{ color: C.sub, fontSize: 13 }}>   {h.icone} {h.nome}</Text>)}
        </View>
      </Recolhe>

      {/* neste dia */}
      {!!antes.length && (
        <View style={{ gap: 8 }}>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>🕰️ {t('NESTE DIA')}</Text>
          {antes.map(x => { const d = lerDia(x.d); const txt = d.quadro?.textos.map(y => y.texto.trim()).find(Boolean) ?? d.respostas?.find(r => r.texto.trim())?.texto ?? '';
            return (
              <Pressable key={x.d} onPress={() => setData(x.d)} style={{ backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12), gap: cx.gap(6) }}>
                <Text style={{ color: cor, fontWeight: '800' }}>{x.rot} · {dataLonga(x.d)} {emojiHumor(d.humor)}</Text>
                {!!txt && <Text style={{ color: C.text }} numberOfLines={3}>{txt}</Text>}
                {!!d.quadro && !txt && <DesenhoView tracos={d.quadro.tracos} alt={Math.min(600, d.quadro.alt)} quadro={d.quadro} />}
              </Pressable>
            ); })}
        </View>
      )}

      {/* lembrete diário */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12) }}>
        <Text style={{ fontSize: 18 }}>🔔</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>{t('Lembrete diário')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{conf.lembrete.on ? t('Todo dia às {h}: "Como foi o seu dia? ✍️"', { h: horaTela(conf.lembrete.hora) }) : t('Desligado')}</Text>
        </View>
        {conf.lembrete.on && <Chip small cor={cor} on={false} label={horaTela(conf.lembrete.hora)} onPress={() => setHora(true)} />}
        <Chip small cor={cor} on={conf.lembrete.on} label={conf.lembrete.on ? t('Ligado') : t('Ligar')} onPress={() => mudarConf({ lembrete: { ...conf.lembrete, on: !conf.lembrete.on } })} />
      </View>

      <Sheet visible={cal} onClose={() => setCal(false)} title={t('Dias escritos')}>
        <View style={{ padding: 16 }}>
          <CalendarioMes data={data} cor={cor} marcados={d => marcados.has(d)} onDia={d => { if (d <= today()) { setData(d); setCal(false); } }} />
          <Text style={{ color: C.sub, fontSize: 12, marginTop: 8 }}>{t('Os dias com página escrita ficam marcados. Toque num dia para abrir.')}</Text>
        </View>
      </Sheet>
      <ClimaSheet visivel={climaEd} cor={cor} valor={dia.clima ?? null} onFechar={() => setClimaEd(false)} onSalvar={c => mudar({ clima: c })} />
      {hora && <PickerModal mode="time" cor={cor} value={(() => { const d = new Date(); const [h, m] = conf.lembrete.hora.split(':').map(Number); d.setHours(h, m, 0, 0); return d; })()}
        onClose={() => setHora(false)} onPick={d => mudarConf({ lembrete: { ...conf.lembrete, on: true, hora: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` } })} />}
    </Screen>
  );
}

/** Trocar o clima à mão: temperatura e condição. */
function ClimaSheet({ visivel, valor, cor, onFechar, onSalvar }: { visivel: boolean; valor: DiaDiario['clima']; cor: string; onFechar: () => void; onSalvar: (c: DiaDiario['clima']) => void }) {
  const [temp, setTemp] = useState<number>(valor?.temp != null ? Math.round(valor.temp) : 22);
  const [cond, setCond] = useState(valor?.cond ?? '');
  const [aberto, setAberto] = useState(visivel);
  if (aberto !== visivel) { setAberto(visivel); if (visivel) { setTemp(valor?.temp != null ? Math.round(valor.temp) : 22); setCond(valor?.cond ?? ''); } }
  return (
    <Sheet visible={visivel} onClose={onFechar} title={t('Clima do dia')}>
      <View style={{ padding: 16, gap: 14 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
          <Pressable onPress={() => setTemp(x => x - 1)} style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 22 }}>−</Text></Pressable>
          <Text style={{ color: C.text, fontSize: 28, fontWeight: '800', width: 90, textAlign: 'center' }}>{temp} °C</Text>
          <Pressable onPress={() => setTemp(x => x + 1)} style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 22 }}>+</Text></Pressable>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {CONDICOES.map(([n, e]) => <Chip key={n} cor={cor} on={cond === n} label={`${e} ${t(n)}`} onPress={() => setCond(n)} />)}
        </View>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('O clima automático usa a localização do celular. Sem permissão, ele fica para preencher aqui.')}</Text>
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Chip cor={cor} on={false} label={t('Limpar')} onPress={() => { onSalvar(null); onFechar(); }} />
          <View style={{ flex: 1 }} />
          <Chip cor={cor} on label={t('Salvar')} onPress={() => { onSalvar({ temp, cond, manual: true }); onFechar(); }} />
        </View>
      </View>
    </Sheet>
  );
}
